import { Button, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useOutletContext } from "react-router-dom";
import { ClaimFollowUpCommentListDto, ClaimFollowUpItemDto, ClaimFileDto, ClaimOutletContext } from "../../types";
import AlertMessage from "../../../../components/AlertMessage";
import { useAlert } from "../../../../hooks/useAlert";
import DocumentsWorkspace from "../../../../components/DocumentsPanels/DocumentsWorkspace";
import { useDocumentsPreview } from "../../../../components/DocumentsPanels/useDocumentsPreview";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { useModal } from "../../../../hooks/useModal";
import { downloadClaimFile, updateClaimFile } from "../../api/ClaimFileAPI";
import ClaimFileDetailsPanel from "../common/ClaimFileDetailsPanel";
import { ClaimFileOriginTypeEnum } from "../../types/enums";
import { getClaimFileContentType } from "../../../../utils/ClaimFileUtils";
import { downloadBase64File } from "../../../../utils/formatters/FileFormatter";
import { SectionPermissionTypeEnum } from "../../../../types/SectionPermissionTypesEnum";

interface EditClaimFollowUpCommentsFilesProps {
    onCancel: () => void;
    claimFollowUpsComment?: ClaimFollowUpCommentListDto | ClaimFollowUpItemDto;
    setClaimFollowUpsComment: React.Dispatch<React.SetStateAction<ClaimFollowUpCommentListDto | ClaimFollowUpItemDto | undefined>>;
    fileOriginType: ClaimFileOriginTypeEnum | null
}

export default function EditClaimFollowUpCommentsFiles({ claimFollowUpsComment, setClaimFollowUpsComment, onCancel, fileOriginType }: EditClaimFollowUpCommentsFilesProps) {
    const { t } = useTranslation(['claim', 'common']);

    const { setHideClaimActionButtons } = useOutletContext<ClaimOutletContext>();
    const { showAlert, hideAlert, alert, isVisible } = useAlert({ fromLocation: false });
    const [loading, setLoading] = useState(false);

    const { showModal, modalData, closeModal, isLoading, errorModal, currentItem, openModal: openDeleteModal } = useModal<ClaimFileDto>();

    const [selectedDocument, setSelectedDocument] = useState<ClaimFileDto | null>(null);

    const fetchDocumentData = useCallback(async (doc: ClaimFileDto) => {
        const downloaded = await downloadClaimFile(doc.id!);
        return { data: downloaded.data, contentType: downloaded.contentType };
    }, []);

    const preview = useDocumentsPreview<ClaimFileDto>({ fetchDocumentData });

    const documents = useMemo(
        () => claimFollowUpsComment?.files?.filter(
            file => fileOriginType === null || file.originType === fileOriginType
        ) ?? [],
        [claimFollowUpsComment?.files, fileOriginType]
    );

    useEffect(() => {
        return () => setHideClaimActionButtons(false);
    }, [setHideClaimActionButtons]);

    const handleBackToList = () => {
        setSelectedDocument(null);
        setHideClaimActionButtons(false);
    };

    const handleFileSaved = (updatedFile: ClaimFileDto) => {
        setClaimFollowUpsComment?.((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                files: (prev.files ?? []).map((file) => {
                    if (updatedFile.id != null && file.id != null) {
                        return file.id === updatedFile.id ? { ...file, ...updatedFile } : file;
                    }

                    return file.fileName === updatedFile.fileName ? { ...file, ...updatedFile } : file;
                }),
            };
        });
    };

    const handleInfo = (doc: ClaimFileDto) => {
        setSelectedDocument(doc);
        setHideClaimActionButtons(true);
        void preview.previewDocument(doc);
    };

    const confirmSubmitModal = async () => {
        try {
            if (modalData.type === "deleteWithoutInput" ) {
                if (!currentItem) return;
                const updated = { ...currentItem, isStarred: !currentItem.isStarred };
                await updateClaimFile(currentItem.id?.toString() ?? "", updated);
                setClaimFollowUpsComment?.((prev) => {
                    if (!prev) return prev;
                    return { ...prev, files: prev.files?.map((f) => f.id === currentItem.id ? { ...f, isStarred: updated.isStarred } : f) };
                });
            }

            closeModal();
        } catch (error) {
            console.error("confirmSubmitModal error:", error);
        }
    };

    const handleDownload = useCallback(async (doc: ClaimFileDto) => {
        if (doc.id == null) return;
        try {
            setLoading(true);
            const downloaded = await downloadClaimFile(doc.id);
            if (!downloaded.data) return;

            const contentType = downloaded.contentType ?? getClaimFileContentType(doc) ?? "application/octet-stream";
            downloadBase64File(doc.fileName ?? "file", downloaded.data, contentType);
        } finally {
            setLoading(false);
        }
    }, []);

    const toggleStarred = async (doc: ClaimFileDto) => {
        if (!doc.isStarred == false) {
            openDeleteModal(doc, t("claim:modal.modalStarFileTitle"),t("claim:modal.modalStarFileSubtitle"), "deleteWithoutInput", "");
        } else {
            if (doc.id == null) return;
            const updated: ClaimFileDto = { ...doc, isStarred: !doc.isStarred };
            try {
                await updateClaimFile(doc.id.toString(), updated);
                setClaimFollowUpsComment?.((prev) => {
                    if (!prev) return prev;
                    return { ...prev, files: prev.files?.map((f) => f.id === doc.id ? { ...f, isStarred: updated.isStarred } : f) };
                });
            } catch {
                setLoading(false);
                showAlert({
                    message: t("common:errors.networkError"),
                    type: "danger",
                    duration: 3000,
                });
            }
        }
    };

   return (
        <>
            <ModalGlobal
                show={showModal}
                onClose={() => {
                    closeModal();
                }}
                title={modalData.title}
                contentType={modalData.type}
                subtitle={modalData.subtitle}
                subtitle2={modalData.subtitle2}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                error={errorModal?.endDate || errorModal?.errorModal}
                onSave={confirmSubmitModal}
                isLoading={isLoading}
            />
            {isVisible && <AlertMessage message={alert!.message} type={alert!.type} onClose={hideAlert} />}

            <DocumentsWorkspace<ClaimFileDto>
                documents={documents}
                preview={preview}
                sectionPermission={SectionPermissionTypeEnum.CLAIMS}
                loading={loading}
                disabledActions={false}
                onInfo={handleInfo}
                onStar={toggleStarred}
                onIcon={() => { }}
                onDownload={handleDownload}
                detailsPanel={selectedDocument
                    ? <ClaimFileDetailsPanel file={selectedDocument} onBack={handleBackToList} onSaved={handleFileSaved} />
                    : undefined}
                previewHeader={
                    <Row>
                        <Col className="text-end mt-2">
                            <Button variant="secondary" onClick={onCancel}>
                                {t("common:buttons.buttonCancel")}
                            </Button>
                        </Col>
                    </Row>
                }
            />
        </>
    );
}
